import {mapObject, groupBy} from '@benchling/js-utils';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ProcessExecutionType, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import * as ProcessManagementContextMenu from 'processes/dashboard/ProcessManagementContextMenu';
import {taskHasExecutionEntry} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import ProcessTaskCellTitle from 'processes/dashboard/ProcessTaskCellTitle';
import ProcessResponsibleParties, {
  ProcessTaskAssigneeDisplay,
  ProcessTaskStatusDisplay,
} from 'processes/dashboard/ProcessTaskDashboardDisplay';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import RequestV2SubmissionChip from 'processes/requests_v2/chips/RequestV2SubmissionChip';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import ProcessChip from 'ui-library/chips/ProcessChip';
import ProcessTaskChip from 'ui-library/chips/ProcessTaskChip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import DateTableCell from 'ui-library/scrollable/table-cells/DateTableCell';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ProcessTaskListingGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';

function EmptyForExecutionTypesWrapper(props: {
  children: JSX.Element;
  task: ProcessTaskListingGraphQL;
  emptyForExecutionTypes: Array<ProcessExecutionType>;
}): JSX.Element {
  return props.emptyForExecutionTypes.includes(props.task.schema.executionType) ? <></> : props.children;
}

export interface ProcessTaskListingExtras extends StudyableItemListingExtras {}

export default class ProcessTaskListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.PROCESS_TASK> {
  listingRendererType = ListingRendererType.PROCESS_TASK;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon="process-task" className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ProcessTaskListingGraphQL>,
    {itemStudyAttributes}: ProcessTaskListingExtras = {}
  ): Array<ColumnConfiguration<ProcessTaskListingGraphQL>> {
    const taskCountsByExecutionType = mapObject(
      groupBy(items, ({schema}) => schema.executionType),
      (tasks) => tasks.length
    );

    const shouldDisplayRequestV2SubmissionColumn =
      isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER}) &&
      items.some((item) => item.process.requestV2Submission != null);

    const allTasksHaveExecutionTypes = (executionTypes: Array<ProcessExecutionType>): boolean => {
      let numTasks = 0;
      for (const executionType of executionTypes) {
        numTasks += taskCountsByExecutionType[executionType] ?? 0;
      }
      return numTasks === items.length;
    };

    const maybeStudiesColumn = maybeGetStudiesListingColumn<ProcessTaskListingGraphQL>(itemStudyAttributes);

    let columns: Array<ColumnConfiguration<ProcessTaskListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          width: 200,
        },
        render: (item) => <ProcessTaskCellTitle processTask={item} title={item.humanId} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_TASK_TYPE,
          header: 'Task type',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_PROCESS_TASK_TYPE,
        },
        render: (item) => processExecutionTypeDisplayName(ProcessClientOrigin.PROCESSES, item.executionType),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEMA,
          header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEMA_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_SCHEMA_NAME,
        },
        render: (item) => item.schema.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_PROCESS_ID),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_PROCESS_ID,
        },
        render: (item) => item.process.humanId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_NAME,
          header: 'Task group name',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => item.process.name,
      },
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
      ...(allTasksHaveExecutionTypes([ProcessExecutionType.FLOWCHART])
        ? []
        : [
            {
              column: {
                accessorKey: ColumnAccessorKey.ASSIGNEE,
                header: getColumnHeaderFromSortKey(SortKey.SORT_ASSIGNEE_NAME),
                flexGrow: 1,
                isSortable: true,
                sortKey: SortKey.SORT_ASSIGNEE_NAME,
              },
              render: (item: ProcessTaskListingGraphQL) => (
                <EmptyForExecutionTypesWrapper
                  task={item}
                  emptyForExecutionTypes={[ProcessExecutionType.FLOWCHART]}
                >
                  <ProcessTaskAssigneeDisplay assignee={item.assignee} />
                </EmptyForExecutionTypesWrapper>
              ),
            },
          ]),
      {
        column: {
          accessorKey: ColumnAccessorKey.STATUS,
          header: getColumnHeaderFromSortKey(SortKey.SORT_STATUS),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_STATUS,
        },
        render: (item) => <ProcessTaskStatusDisplay status={item.status} />,
      },
      ...(shouldDisplayRequestV2SubmissionColumn
        ? [
            {
              column: {
                accessorKey: ColumnAccessorKey.REQUEST_V2_SUBMISSION,
                header: 'Request',
                flexGrow: 1,
                isSortable: false,
              },
              render: (item: ProcessTaskListingGraphQL) => {
                if (item.process.requestV2Submission != null) {
                  return (
                    <RequestV2SubmissionChip
                      id={getAccessibleOrInaccessibleId(item.process.requestV2Submission)}
                      size={ChipSize.MINI}
                    />
                  );
                }
                return null;
              },
            },
          ]
        : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.ROOT_PROCESS_TASK,
          header: 'Flowchart task',
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) =>
          item.rootTask != null ? (
            <ProcessTaskChip id={getAccessibleOrInaccessibleId(item.rootTask)} size={ChipSize.MINI} />
          ) : null,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ROOT_PROCESS,
          header: 'Flowchart task group',
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) =>
          item.process.rootProcess != null ? (
            <ProcessChip id={getAccessibleOrInaccessibleId(item.process.rootProcess)} size={ChipSize.MINI} />
          ) : null,
      },
      ...(allTasksHaveExecutionTypes([ProcessExecutionType.FLOWCHART])
        ? []
        : [
            {
              column: {
                accessorKey: ColumnAccessorKey.SCHEDULED_ON,
                header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEDULED_ON_KEY),
                flexGrow: 1,
                isSortable: true,
                sortKey: SortKey.SORT_SCHEDULED_ON_KEY,
              },
              render: (item: ProcessTaskListingGraphQL) => (
                <EmptyForExecutionTypesWrapper
                  task={item}
                  emptyForExecutionTypes={[ProcessExecutionType.FLOWCHART]}
                >
                  <DateTableCell date={item.scheduledOnDate} />
                </EmptyForExecutionTypesWrapper>
              ),
            },
          ]),
      ...((taskCountsByExecutionType[ProcessExecutionType.ENTRY] ?? 0) > 0
        ? [
            {
              column: {
                accessorKey: ColumnAccessorKey.EXECUTION_ENTRY,
                header: 'Execution Entry',
                isSortable: false,
                flexGrow: 1,
              },
              render: (item: ProcessTaskListingGraphQL) =>
                taskHasExecutionEntry(item) ? (
                  <EntryChip
                    onClick={(e, chipItem) => {
                      e.stopPropagation();
                      FileBrowserActions.openFile(chipItem.id);
                      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_EXECUTION_ENTRY_CHIP, {
                        process_client_origin: item.schema.clientOrigin,
                      });
                    }}
                    id={item.executionOrigin.executionEntry.id}
                    size={ChipSize.TABLE_CELL}
                  />
                ) : (
                  <div />
                ),
            },
          ]
        : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.TASK_RESPONSIBLE_PARTIES,
          header: 'Responsible parties',
          isSortable: false,
          flexGrow: 1,
        },
        render: (item: ProcessTaskListingGraphQL) => (
          <ProcessResponsibleParties responsibleParties={item.responsibleParties} />
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];
    const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(items);
    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column,
          render: (item: ProcessTaskListingGraphQL) => {
            const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
              tags: item.fields,
              tagColumns: [column],
            });
            return tagsByColumn.tagFields[column.accessorKey];
          },
        }))
      );
    }

    return columns;
  }

  getNonPluginRowContentHeight(
    item: ProcessTaskListingGraphQL,
    {itemStudyAttributes}: ProcessTaskListingExtras
  ): number {
    return Math.max(
      ExpandedTableTagUtil.getRowHeight(item.fields),
      getStudiesRowContentHeight(item.id, itemStudyAttributes)
    );
  }

  openContextMenu(item: ProcessTaskListingGraphQL, e: BasicEvent, extras: unknown): void {
    ProcessManagementContextMenu.open(e.nativeEvent, ModelTypeConstants.PROCESS_TASK, item);
  }
}
